import { ROTULO_DIA, dentroDoHorario, type Configuracoes, type DiaSemana } from '@viniko/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useToast } from '../../componentes/ui';
import { patch } from '../../lib/api';
import { chaves, useConfiguracoes } from '../../lib/consultas';

const ORDEM: DiaSemana[] = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'];

export function Atendimento() {
  const qc = useQueryClient();
  const toast = useToast();
  const salvo = useConfiguracoes().data;
  const [cfg, setCfg] = useState<Configuracoes | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (salvo && !cfg) setCfg(salvo);
  }, [salvo, cfg]);

  if (!cfg) return <div className="config-cartao">Carregando…</div>;

  const mudarDia = (d: DiaSemana, valor: Configuracoes['horario']['dias'][DiaSemana]) =>
    setCfg({ ...cfg, horario: { ...cfg.horario, dias: { ...cfg.horario.dias, [d]: valor } } });

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      const novo = await patch<Configuracoes>('/api/configuracoes', cfg);
      qc.setQueryData(chaves.configuracoes, novo);
      setCfg(novo);
      toast('Configurações salvas');
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  const aberto = dentroDoHorario(cfg);

  return (
    <div className="config-cartao largo">
      <header>
        <h2>Atendimento</h2>
        <p>Horário da equipe, IA e tempos de espera da fila.</p>
      </header>

      <fieldset className="grupo">
        <legend>Assistente com IA</legend>
        <label className="interruptor">
          <input
            id="cfg-ia"
            type="checkbox"
            checked={cfg.ia.ativa}
            onChange={(e) => setCfg({ ...cfg, ia: { ...cfg.ia, ativa: e.target.checked } })}
          />
          <span className="trilho" aria-hidden />
          <span>
            <b>{cfg.ia.ativa ? 'IA ligada' : 'IA desligada'}</b>
            <small>
              {cfg.ia.ativa
                ? 'A IA atende o primeiro contato, coleta o orçamento e passa pra fila. Fora do horário, ela também atende.'
                : 'Toda conversa nova vai direto pra fila. Fora do horário, o cliente recebe a mensagem abaixo.'}
            </small>
          </span>
        </label>
        {cfg.ia.ativa && (
          <label className="interruptor">
            <input
              id="cfg-ia-fila"
              type="checkbox"
              checked={cfg.ia.duvidasNaFila}
              onChange={(e) =>
                setCfg({ ...cfg, ia: { ...cfg.ia, duvidasNaFila: e.target.checked } })
              }
            />
            <span className="trilho" aria-hidden />
            <span>
              <b>Tirar dúvidas enquanto o cliente espera</b>
              <small>
                {cfg.ia.duvidasNaFila
                  ? 'Na fila, a IA responde dúvidas simples e anota dados novos do orçamento. Não fala preço nem prazo e para de vez quando alguém assume.'
                  : 'Depois de passar pra fila, a IA fica quieta até alguém assumir.'}
              </small>
            </span>
          </label>
        )}
      </fieldset>

      <fieldset className="grupo">
        <legend>
          Horário da equipe{' '}
          <span className={`agora ${aberto ? 'aberto' : 'fechado'}`}>
            {aberto ? 'aberto agora' : 'fechado agora'}
          </span>
        </legend>
        <div className="horarios">
          {ORDEM.map((d) => {
            const p = cfg.horario.dias[d];
            return (
              <div key={d} className="horario-linha">
                <label className="check">
                  <input
                    id={`cfg-dia-${d}`}
                    type="checkbox"
                    checked={!!p}
                    onChange={(e) =>
                      mudarDia(d, e.target.checked ? { abre: '07:00', fecha: '16:30' } : null)
                    }
                  />
                  {ROTULO_DIA[d]}
                </label>
                {p ? (
                  <span className="horas">
                    <input
                      id={`cfg-abre-${d}`}
                      type="time"
                      className="input"
                      value={p.abre}
                      onChange={(e) => mudarDia(d, { ...p, abre: e.target.value })}
                    />
                    <span>até</span>
                    <input
                      id={`cfg-fecha-${d}`}
                      type="time"
                      className="input"
                      value={p.fecha}
                      onChange={(e) => mudarDia(d, { ...p, fecha: e.target.value })}
                    />
                  </span>
                ) : (
                  <span className="fechado-txt">Fechado</span>
                )}
              </div>
            );
          })}
        </div>
        <label className="campo">
          <span>Mensagem de fora do horário</span>
          <textarea
            id="cfg-mensagem-fora"
            className="input"
            rows={2}
            maxLength={500}
            value={cfg.horario.mensagemFora}
            onChange={(e) =>
              setCfg({ ...cfg, horario: { ...cfg.horario, mensagemFora: e.target.value } })
            }
          />
          <small>
            Vai pro cliente quando a conversa entra na fila fora do horário. Horário de Brasília.
          </small>
        </label>
      </fieldset>

      <fieldset className="grupo">
        <legend>Tempo de espera na fila</legend>
        <div className="tempos">
          <label className="campo">
            <span>
              <i className="ponto atencao" /> Amarelo a partir de
            </span>
            <span className="com-unidade">
              <input
                id="cfg-atencao"
                type="number"
                min={1}
                max={240}
                className="input"
                value={cfg.fila.atencaoMin}
                onChange={(e) =>
                  setCfg({ ...cfg, fila: { ...cfg.fila, atencaoMin: Number(e.target.value) } })
                }
              />
              min
            </span>
          </label>
          <label className="campo">
            <span>
              <i className="ponto critico" /> Vermelho a partir de
            </span>
            <span className="com-unidade">
              <input
                id="cfg-critico"
                type="number"
                min={2}
                max={480}
                className="input"
                value={cfg.fila.criticoMin}
                onChange={(e) =>
                  setCfg({ ...cfg, fila: { ...cfg.fila, criticoMin: Number(e.target.value) } })
                }
              />
              min
            </span>
          </label>
        </div>
      </fieldset>

      {erro && <div className="erro-form">{erro}</div>}
      <div>
        <button className="btn primario" disabled={salvando} onClick={() => void salvar()}>
          Salvar
        </button>
      </div>
    </div>
  );
}
